import React from 'react';
import { Bot, Map, Sofa, Inbox } from 'lucide-react';

const CAPABILITIES = [
    { icon: Bot, label: 'AI Agent Guide' },
    { icon: Map, label: 'Interactive Floor Plans' },
    { icon: Sofa, label: 'Virtual Staging' },
    { icon: Inbox, label: 'Lead Capture' },
] as const;

/**
 * Split-screen shell for the 360Tours Studio sign-in and sign-up pages.
 * The right-hand slot is the authentication form. Visual only.
 */
const ToursAuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <div className="min-h-screen w-full bg-[#0c0b10] text-white lg:grid lg:h-screen lg:grid-cols-[minmax(0,58fr)_minmax(0,42fr)] lg:overflow-hidden">
            <aside className="relative flex flex-col px-5 pb-4 pt-5 sm:px-8 sm:pt-6 lg:h-screen lg:px-10 lg:py-8">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            'radial-gradient(ellipse 70% 45% at 15% 0%, rgba(236,72,153,0.18), transparent 58%), radial-gradient(ellipse 55% 40% at 90% 100%, rgba(168,85,247,0.14), transparent 55%)',
                    }}
                />

                <div className="relative z-10 flex min-h-0 flex-1 flex-col">
                    <img
                        src="/360tours-studio-logo-transparent.png"
                        alt="360Tours Studio"
                        className="h-16 w-auto max-w-[280px] object-contain object-left sm:h-20 lg:h-20 lg:max-w-[320px]"
                    />

                    <h1 className="mt-8 max-w-[12em] text-[1.7rem] font-semibold leading-[1.12] tracking-tight text-white lg:mt-9 lg:text-[2.15rem]">
                        <span className="lg:hidden">Create immersive 360° experiences</span>
                        <span className="hidden lg:block">
                            Create immersive
                            <br />
                            360° experiences
                            <br />
                            for any space
                        </span>
                    </h1>
                    <p className="mt-4 hidden max-w-[46ch] text-sm leading-relaxed text-white/65 lg:block">
                        Build interactive 360° tours with floor plans, AI guides, virtual staging and built-in enquiries.
                    </p>

                    <div className="mt-6 hidden w-full lg:block">
                        <img
                            src="/auth-viewer.jpg"
                            alt="360Tours Studio viewer showing a property tour with scene navigation, hotspots and an AI guide"
                            className="h-auto max-h-[calc(100vh-29rem)] w-auto max-w-[78%] rounded-2xl border border-white/10 object-contain object-left shadow-[0_16px_36px_-24px_rgba(0,0,0,0.7)]"
                        />
                    </div>

                    <ul className="relative mt-6 hidden grid-cols-2 gap-x-6 gap-y-3 sm:grid lg:flex lg:flex-nowrap lg:items-center lg:justify-between lg:gap-x-4">
                        {CAPABILITIES.map(({ icon: Icon, label }) => (
                            <li key={label} className="flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-white/75">
                                <Icon size={14} strokeWidth={1.75} className="shrink-0 text-[#f472b6]" aria-hidden />
                                <span>{label}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="relative mt-8 hidden lg:mt-auto lg:block lg:pt-5">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/40">
                            Part of ReHub Studio
                        </p>
                        <img
                            src="/ReHub%20Logo%20Transparent%20White%20Text%20.png"
                            alt="ReHub Studio"
                            className="mt-3 h-6 w-auto object-contain object-left opacity-60"
                        />
                    </div>
                </div>
            </aside>

            <section className="flex flex-col bg-[#f7f6f4] text-slate-900 lg:h-screen lg:overflow-y-auto">
                <div className="flex flex-1 items-center justify-center px-5 py-8 sm:px-8 lg:px-10">
                    <div className="w-full max-w-[420px]">{children}</div>
                </div>
                <p className="flex items-center justify-center gap-2 pb-5 text-[11px] text-slate-400 lg:hidden">
                    <img
                        src="/ReHub%20Logo%20Transparent%20White%20Text%20.png"
                        alt=""
                        className="h-4 w-auto object-contain brightness-0 opacity-40"
                    />
                    Part of ReHub Studio
                </p>
            </section>
        </div>
    );
};

export default ToursAuthShell;
